<template>
    <v-hover v-slot:default="{ hover }">
        <div>
            <div
                class="time-info-wrapper"
                :class="overlappingInfo ? overlappingInfo.type : ''"
            >
                <div class="time-info" />
            </div>
            <action-menu
                :items="bubbleOptions"
                :title="title"
                wide
                @action="bubbleOptions[$event].action(item)"
            >
                <template v-slot:activator="{ on }">
                    <div v-show="hover" v-on="on" class="time-info-bubble-wrapper" :style="`left: ${tableSize(hover)}px`">
                        <v-btn color="primary" text style="min-width: unset" class="px-0">
                            <v-icon size="16" color="white">mdi-dots-vertical</v-icon>
                        </v-btn>
                    </div>
                </template>
                <template v-slot:bottom="">
                    <a href="/api/help_link/time_feedback" target="_blank" style="text-decoration: none">
                        <v-list-item style="border-top: 1px solid var(--v-g3-base)" class="pt-1 mt-2">
                            <v-list-item-title class="d-flex align-center body-2 font-weight-regular">
                                <v-icon size="21" class="mr-2">mdi-information-outline</v-icon>
                                {{ $t('Read more') }} ›
                            </v-list-item-title>
                        </v-list-item>
                    </a>
                </template>
            </action-menu>
        </div>
    </v-hover>
</template>

<script>
import {parseSysDateTime} from "@/plugins/formatting";
import ApiTimeEntry from "@/api/time_entry/timeEntry";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import {$t} from "@/i18n";
import TaskModule from "@/store/modules/task";
import {computed, onMounted, ref, watch} from "@vue/composition-api";

export default {
    name: "RichDataTableTimeFeedback",
    props: {
        item: {
            type: Object,
            required: true
        },
        nextItem: {
            type: Object,
            default: null
        },
        showTimeFeedback: {
            type: Boolean,
            default: true
        },
        showTimeFeedbackOptions: {
            type: Boolean,
            default: true
        },
        showNewDayFeedback: {
            type: Boolean,
            default: false
        }
    },
    setup(props, ctx) {
        const taskModule = TaskModule();
        const watchTaskAdd = ref(false);

        const tableSize = (hover) => {
            const tableElement = ctx.parent.$parent.$el.querySelector('.v-data-table__wrapper')
            return tableElement.getBoundingClientRect().width + tableElement.scrollLeft - 50
        }

        const overlappingInfo = computed(() => {
            if (!props.nextItem) {
                return null
            }

            if (!props.item.end) {
                return null
            }

            if (!props.item.start) {
                return null
            }

            if (!props.showTimeFeedback && !props.showNewDayFeedback) {
                return null
            }

            const currentItemEnd = parseSysDateTime(props.item.end)
            const nextItemStart = parseSysDateTime(props.nextItem.start)

            if (currentItemEnd.getDate() !== nextItemStart.getDate()) {
                return {
                    type: 'day-gap',
                    item: props.item,
                    nextItem: props.nextItem
                }
            }

            if (!props.showTimeFeedback) {
                return null
            }

            const currentItemEndPaddedTimeGap = new Date(currentItemEnd.getTime() + 1000 * 61)
            if (currentItemEndPaddedTimeGap < nextItemStart) {
                return {
                    type: 'time-gap',
                    item: props.item,
                    nextItem: props.nextItem
                }
            }

            const currentItemEndPadded = new Date(currentItemEnd.getTime() - 1000 * 61)
            if (currentItemEndPadded > nextItemStart) {
                return {
                    type: 'time-overlap',
                    item: props.item,
                    nextItem: props.nextItem
                }
            }
        })

        const saveTimeLog = async (item, start, end) => {
            try {
                await ApiTimeEntry.saveTimeEntry({
                    billable: item.billable,
                    person_id: item.person_id,
                    task_id: item.task_id,
                    id: item.id,
                    start: start,
                    end: end
                })
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const saveTimeLogOverlapNext = async (item, start, end) => {
            try {
                await ApiTimeEntry.saveTimeEntryOverlapNext({
                    billable: item.billable,
                    person_id: item.person_id,
                    task_id: item.task_id,
                    id: item.id,
                    start: start,
                    end: end
                })
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const openTaskWithTimeEntry = (item, start, end) => {
            taskModule.openTask(null, {
                timeEntry: {
                    billable: 1,
                    end: end,
                    id:0,
                    person_id: item.person_id,
                    start: start,
                    task_id:0
                }
            })

            watchTaskAdd.value = true
        }

        watch (taskModule.current_task, (newValue, oldValue) => {
            if (watchTaskAdd.value && oldValue && oldValue.just_added) {
                ctx.emit('updateTasks')
            } else if (watchTaskAdd.value && !newValue) {
                watchTaskAdd.value = false
            }
        }, { immediate: true })

        const bubbleOptions = computed(() => {
            let items = []

            if (!overlappingInfo.value) {
                return items
            }

            if (!props.showTimeFeedbackOptions) {
                return items
            }

            if (overlappingInfo.value.type === 'time-gap') {
                items.push({
                    text: $t('Add to previous time entry'),
                    action: () => saveTimeLog(props.item, props.item.start, props.nextItem.start)
                })

                items.push({
                    text: $t('Add to next time entry'),
                    action: () => saveTimeLog(props.nextItem, props.item.end, props.nextItem.end)
                })

                items.push({
                    text: $t('Add new task'),
                    action: () => openTaskWithTimeEntry(props.item, props.item.end, props.nextItem.start)
                })
            }

            if (overlappingInfo.value.type === 'time-overlap') {
                items.push({
                    text: $t('Cut previous time entry'),
                    action: () => saveTimeLog(props.item, props.item.start, props.nextItem.start)
                })

                items.push({
                    text: $t('Cut next time entry'),
                    action: () => saveTimeLogOverlapNext(props.item, props.item.end, props.nextItem.end)
                })
            }

            return items
        })

        const title = computed(() => {
            if (!overlappingInfo.value) {
                return null
            }

            if (overlappingInfo.value.type === 'time-gap') {
                return $t('Missing time entry')
            } else if (overlappingInfo.value.type === 'time-overlap') {
                return $t('Overlapping time entries')
            }
        })

        return {
            bubbleOptions,
            overlappingInfo,
            tableSize,
            watchTaskAdd,
            title
        }
    }
};
</script>

<style scoped lang="scss">
.time-info-bubble-wrapper {
    position: absolute;
    padding: 8px;
    bottom: 1px;
    z-index: 3;
    transform: translateX(-10%) translateY(50%);

    .v-btn {
        background-color: var(--v-primary-base) !important;
        min-height: unset;
        height: 20px;
        width: 20px;
    }

    .time-info-bubble {
        border-radius: 100%;
        background-color: var(--v-primary-base);
        width: 8px;
        height: 8px;

    }
}

.time-info-wrapper {
    position:absolute;
    bottom: 0;
    right: 0;
    padding: 5px 0;
    cursor: pointer;

    &.time-overlap {
        width: 20%;
        bottom: -5px;

        .time-info {
            height: 1px;
            background-color: var(--v-error-base);
        }
    }

    &.time-gap {
        background-image: linear-gradient(to right, var(--v-tretiary-base) 50%, rgb(59, 207, 94, 0) 0%);
        background-position: bottom;
        background-size: 4px 5px;
        background-repeat: repeat-x;
        width: 20%;
        height: 5px;
        bottom: -2px;

        .time-info {

        }
    }

    &.day-gap {
        left: 0;
        bottom: -5px;

        .time-info {
            height: 2px;
            background-color: var(--v-main-base);
        }
    }
}
</style>